<template>
  <div class="carousel-block py-3">
    <h2 v-if="block.value.heading" class="h4 mt-0 mb-3">{{ block.value.heading }}</h2>
    <div class="row g-3" :class="columnClasses">
      <div v-for="(option, i) in options" :key="i" class="col">
        <div class="carousel-card card h-100 position-relative">
          <div class="card-body d-flex flex-column">
            <div
              v-if="option.badge || option.value.target"
              class="d-flex align-items-center gap-2 mb-2"
            >
              <span v-if="option.badge" class="badge" :class="option.badge.class">
                {{ option.badge.label }}
              </span>
              <span v-if="option.value.target" class="text-muted small">
                {{ option.value.target }}
              </span>
            </div>
            <h3 class="h4 mt-0">{{ option.value.title }}</h3>
            <div
              class="carousel-description flex-grow-1"
              @click="onInternalLinkClick"
              v-html="option.value.description"
            ></div>
            <component
              :is="option.value.external_url ? 'a' : 'router-link'"
              v-if="option.showButton"
              :to="option.value.internal_page_url"
              :href="option.value.external_url || undefined"
              :target="option.value.external_url ? '_blank' : undefined"
              :rel="option.value.external_url ? 'noopener noreferrer' : undefined"
              class="carousel-readmore stretched-link mt-3 align-self-start"
            >
              <span>{{ option.value.link_label }}</span>
            </component>
            <div v-if="option.src" class="carousel-image-container">
              <img :src="option.src" :alt="option.image_alt" class="carousel-image" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script lang="ts" setup>
import {SphereBlock} from '@/stores/apolloPlatform/cores/portalNavigation';
import {useLocalisation} from '@/composables/useLocalisation';
import {useInternalLinks} from '@/composables/useInternalLinks';
import {getStatusBadge, hasLink} from './featureCard';

const {block} = defineProps<{block: SphereBlock<'carousel_block'>}>();
const {translate} = useLocalisation();
const {onInternalLinkClick} = useInternalLinks();

const options = computed(() =>
  block.value.options.map((value) => ({
    value,
    src: value.src,
    image_alt: value.image_alt,
    badge: getStatusBadge(value.status, translate),
    showButton: hasLink(value),
  })),
);

// 2 -> two columns, 3 -> three columns, 4 -> two columns, four on wide screens.
const columnClasses = computed(() => {
  const n = block.value.options.length;
  if (n === 3) return 'row-cols-1 row-cols-md-3';
  if (n >= 4) return 'row-cols-1 row-cols-md-2 row-cols-xl-4';
  return 'row-cols-1 row-cols-md-2';
});
</script>
<style lang="scss" scoped>
// The whole card is one click target (stretched link); links inside the description stay clickable.
.carousel-card {
  border: 0;
  border-radius: 10px;
  background-color: var(--bs-body-bg);
  box-shadow: -4px 4px 15px 0 rgb(0 0 0 / 10%);
  color: var(--bs-body-color);
}

.carousel-card .card-body {
  padding: 1.5rem 1rem 1rem;
}

.carousel-description {
  font-size: 20px;

  :deep(a) {
    position: relative;
    z-index: 2;
  }

  :deep(p:last-child) {
    margin-bottom: 0;
  }
}

.carousel-card .carousel-readmore {
  color: var(--ekco-blue);
  font-size: 20px;
  font-weight: 500;
  text-decoration: underline;
  text-decoration-color: var(--ekco-blue);

  &:hover,
  &:focus-visible {
    color: var(--ekco-blue);
    text-decoration: underline;
  }
}

.carousel-image-container {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: auto;
  padding-top: 2rem;
}

/* Same frame for every image, whatever its upload size; cropped, not stretched. */
.carousel-image {
  width: 100%;
  aspect-ratio: 16 / 9;
  object-fit: cover;
  border-radius: 10px;
}
</style>
